/**
 * Post-process sanitized blog HTML so every `<a href>` has meaningful DOM text for crawlers
 * (Semrush "no anchor text") and accessibility, without changing visible layout.
 *
 * DOMParser-only (no cheerio) so the renderer never ships ~90KB of HTML parsers on public pages.
 * Server callers must ensure `DOMParser` exists (see sanitizeBlogHtml.js).
 */
const SR_ONLY_CLASS = 'apt-sr-only';

function hasMeaningfulText(str: string): boolean {
  const t = str.replace(/\s+/g, ' ').trim();
  if (!t) return false;
  return /[\p{L}\p{N}]/u.test(t);
}

function deriveLinkLabel(anchor: Element, href: string): string {
  const title = (anchor.getAttribute('title') || '').trim();
  if (title) return title;
  const img = anchor.querySelector('img');
  const imgAlt = (img?.getAttribute('alt') || '').trim();
  if (imgAlt) return imgAlt;
  if (href) {
    try {
      const u = new URL(href, 'https://americanpolicetraining.com');
      const host = u.hostname.replace(/^www\./, '');
      if (host) return host;
      const parts = u.pathname.split('/').filter(Boolean);
      if (parts.length) {
        const last = parts[parts.length - 1] ?? '';
        const readable = last.replace(/[-_]+/g, ' ').trim();
        if (readable) return readable;
      }
    } catch {
      /* ignore */
    }
  }
  return 'Link';
}

function requireDOMParser(): typeof DOMParser {
  if (typeof DOMParser !== 'undefined') {
    return DOMParser;
  }
  throw new Error(
    'ensureBlogAnchorsHaveAccessibleText requires DOMParser (browser or jsdom polyfill)',
  );
}

/**
 * Inserts a visually hidden span as the first child of anchors that have no letter/digit text.
 * Idempotent: skips anchors that already contain `.apt-sr-only`.
 */
export function ensureBlogAnchorsHaveAccessibleText(html: string): string {
  if (!html || typeof html !== 'string') {
    return html;
  }
  const Parser = requireDOMParser();
  const doc = new Parser().parseFromString(
    `<div data-apt-blog-root>${html}</div>`,
    'text/html',
  );
  const root = doc.querySelector('[data-apt-blog-root]');
  if (!root) {
    return html;
  }
  root.querySelectorAll('a[href]').forEach((anchor) => {
    if (anchor.querySelector(`.${SR_ONLY_CLASS}`)) {
      return;
    }
    const visibleText = (anchor.textContent || '').replace(/\s+/g, ' ').trim();
    if (hasMeaningfulText(visibleText)) {
      return;
    }
    const href = anchor.getAttribute('href') || '';
    const label = deriveLinkLabel(anchor, href);
    const span = doc.createElement('span');
    span.className = SR_ONLY_CLASS;
    span.textContent = label;
    anchor.insertBefore(span, anchor.firstChild);
  });
  return root.innerHTML;
}
